/**
 * ProjectManager Component
 *
 * Admin component for comprehensive project management:
 * - View and manage projects (list, create, edit, archive)
 * - Assign agents to projects with roles
 * - Activate/deactivate agent work on projects
 * - View project channels and members
 *
 * @module components/admin/ProjectManager
 */

import { useState, useCallback, useMemo, memo, useEffect } from 'react';
import {
  FolderKanban,
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  Archive,
  ArchiveRestore,
  Trash2,
  Edit3,
  Bot,
  Users,
  Hash,
  Play,
  Pause,
  UserPlus,
  UserMinus,
  ExternalLink,
  GitBranch,
  FolderOpen
} from 'lucide-react';

// ============================================
// Constants
// ============================================

const API_BASE = '/api';

const PROJECT_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  ARCHIVED: 'archived'
};

// ============================================
// API Functions
// ============================================

async function fetchProjects(includeArchived = false) {
  const res = await fetch(`${API_BASE}/projects?includeArchived=${includeArchived}`);
  if (!res.ok) throw new Error('Failed to fetch projects');
  return res.json();
}

async function createProject(data) {
  const res = await fetch(`${API_BASE}/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to create project');
  }
  return res.json();
}

async function updateProject(id, data) {
  const res = await fetch(`${API_BASE}/projects/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to update project');
  }
  return res.json();
}

async function deleteProject(id) {
  const res = await fetch(`${API_BASE}/projects/${id}`, {
    method: 'DELETE'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to delete project');
  }
  return true;
}

async function fetchProjectAgents(projectId) {
  const res = await fetch(`${API_BASE}/projects/${projectId}/agents`);
  if (!res.ok) throw new Error('Failed to fetch project agents');
  return res.json();
}

async function fetchProjectChannels(projectId) {
  const res = await fetch(`${API_BASE}/projects/${projectId}/channels`);
  if (!res.ok) throw new Error('Failed to fetch project channels');
  return res.json();
}

async function assignAgentToProject(projectId, agentId, roleId) {
  const res = await fetch(`${API_BASE}/projects/${projectId}/agents/${agentId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roleId })
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to assign agent');
  }
  return res.json();
}

async function removeAgentFromProject(projectId, agentId) {
  const res = await fetch(`${API_BASE}/projects/${projectId}/agents/${agentId}`, {
    method: 'DELETE'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to remove agent');
  }
  return true;
}

async function activateAgentOnProject(projectId, agentId) {
  const res = await fetch(`${API_BASE}/projects/${projectId}/agents/${agentId}/activate`, {
    method: 'PUT'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to activate agent');
  }
  return res.json();
}

async function deactivateAgentFromProject(projectId, agentId) {
  const res = await fetch(`${API_BASE}/projects/${projectId}/agents/${agentId}/deactivate`, {
    method: 'PUT'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to deactivate agent');
  }
  return res.json();
}

async function fetchAgents() {
  const res = await fetch(`${API_BASE}/agents?status=active`);
  if (!res.ok) throw new Error('Failed to fetch agents');
  return res.json();
}

async function fetchRoles() {
  const res = await fetch(`${API_BASE}/roles`);
  if (!res.ok) throw new Error('Failed to fetch roles');
  return res.json();
}

// ============================================
// Sub-Components
// ============================================

const StatusMessage = memo(function StatusMessage({ type, message, onDismiss }) {
  const config = {
    success: {
      bg: 'bg-green-500/10',
      border: 'border-green-500/30',
      text: 'text-green-400',
      icon: CheckCircle
    },
    error: {
      bg: 'bg-red-500/10',
      border: 'border-red-500/30',
      text: 'text-red-400',
      icon: AlertCircle
    },
    info: {
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      text: 'text-blue-400',
      icon: AlertCircle
    }
  };

  const { bg, border, text, icon: Icon } = config[type] || config.info;

  return (
    <div className={`flex items-center gap-3 px-4 py-3 ${bg} ${border} border rounded-lg`}>
      <Icon className={`w-5 h-5 ${text} flex-shrink-0`} />
      <span className={`flex-1 text-sm ${text}`}>{message}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className={`p-1 ${text} hover:bg-gray-700/50 rounded transition-colors`}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
});

const ProjectForm = memo(function ProjectForm({
  project,
  onSubmit,
  onCancel,
  isProcessing
}) {
  const [formData, setFormData] = useState({
    name: project?.name || '',
    displayName: project?.displayName || '',
    description: project?.description || '',
    repositoryUrl: project?.repositoryUrl || '',
    isActive: project?.isActive ?? true
  });

  const isEdit = !!project;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">
            Name (slug) *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            disabled={isEdit}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            placeholder="my-project"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">
            Display Name
          </label>
          <input
            type="text"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="My Project"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          rows={2}
          placeholder="Project description..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">
          <GitBranch className="w-4 h-4 inline mr-1" />
          Repository URL
        </label>
        <input
          type="text"
          value={formData.repositoryUrl}
          onChange={(e) => setFormData({ ...formData, repositoryUrl: e.target.value })}
          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="https://github.com/..."
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-300">
        <input
          type="checkbox"
          checked={formData.isActive}
          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
          className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-indigo-600 focus:ring-indigo-500"
        />
        Active
      </label>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isProcessing || !formData.name.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50"
        >
          {isProcessing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          {isEdit ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
});

const AssignAgentModal = memo(function AssignAgentModal({
  isOpen,
  projectName,
  agents,
  roles,
  assignedAgentIds,
  onAssign,
  onCancel,
  isProcessing
}) {
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedAgentId('');
      setSelectedRoleId('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const availableAgents = agents.filter(a => !assignedAgentIds.has(a.id));
  const projectRoles = roles.filter(r => r.scope === 'project' || r.scope === 'global');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-green-400" />
            Assign Agent to "{projectName}"
          </h3>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Agent
            </label>
            <select
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select agent...</option>
              {availableAgents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.displayName || agent.name}
                </option>
              ))}
            </select>
            {availableAgents.length === 0 && (
              <p className="text-xs text-amber-400 mt-1">All agents are already assigned</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Role
            </label>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select role...</option>
              {projectRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.displayName || role.name} ({role.scope})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onAssign(selectedAgentId, selectedRoleId)}
            disabled={isProcessing || !selectedAgentId || !selectedRoleId}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-500 rounded-lg transition-colors disabled:opacity-50"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <UserPlus className="w-4 h-4" />
            )}
            Assign
          </button>
        </div>
      </div>
    </div>
  );
});

const DeleteConfirmModal = memo(function DeleteConfirmModal({
  isOpen,
  projectName,
  onConfirm,
  onCancel,
  isProcessing
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-400" />
            Delete Project
          </h3>
        </div>

        <div className="px-6 py-4">
          <p className="text-gray-300">
            Are you sure you want to delete the project <strong className="text-white">"{projectName}"</strong>?
          </p>
          <p className="text-sm text-amber-400 mt-3">
            Warning: This will remove all role assignments. Channels will be unlinked but not deleted.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isProcessing}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors disabled:opacity-50"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
});

const ProjectItem = memo(function ProjectItem({
  project,
  isExpanded,
  onToggle,
  onEdit,
  onArchive,
  onDelete,
  projectAgents,
  projectChannels,
  onAssignAgent,
  onRemoveAgent,
  onActivateAgent,
  onDeactivateAgent,
  isLoadingDetails,
  isProcessing
}) {
  const getStatusBadge = () => {
    if (project.isArchived) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-gray-500/20 text-gray-400 border border-gray-500/30">
          <Archive className="w-3 h-3 mr-1" />
          Archived
        </span>
      );
    }
    if (!project.isActive) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
          Inactive
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-green-500/20 text-green-400 border border-green-500/30">
        Active
      </span>
    );
  };

  return (
    <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 hover:bg-gray-800 transition-colors cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          <FolderKanban className="w-5 h-5 text-indigo-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-200 truncate">
                {project.displayName || project.name}
              </span>
              {getStatusBadge()}
            </div>
            {project.description && (
              <p className="text-xs text-gray-500 truncate mt-0.5">{project.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-1" title="Channels">
            <Hash className="w-4 h-4" />
            <span>{project.channelCount || 0}</span>
          </div>
          <div className="flex items-center gap-1" title="Members">
            <Users className="w-4 h-4" />
            <span>{project.memberCount || 0}</span>
          </div>

          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => onEdit(project)}
              disabled={isProcessing}
              className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors disabled:opacity-50"
              title="Edit project"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onArchive(project)}
              disabled={isProcessing}
              className="p-1.5 text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 rounded transition-colors disabled:opacity-50"
              title={project.isArchived ? 'Unarchive' : 'Archive'}
            >
              {project.isArchived ? (
                <ArchiveRestore className="w-4 h-4" />
              ) : (
                <Archive className="w-4 h-4" />
              )}
            </button>
            <button
              type="button"
              onClick={() => onDelete(project)}
              disabled={isProcessing}
              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
              title="Delete project"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 py-4 bg-gray-900/50 border-t border-gray-700/50 space-y-4">
          {/* Project Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            {project.repositoryUrl && (
              <div>
                <span className="text-gray-500">Repository:</span>
                <a
                  href={project.repositoryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
                >
                  {project.repositoryUrl.replace(/^https?:\/\//, '').slice(0, 40)}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>

          {isLoadingDetails ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
            </div>
          ) : (
            <>
              {/* Assigned Agents */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
                    Assigned Agents ({projectAgents?.length || 0})
                  </h4>
                  <button
                    type="button"
                    onClick={onAssignAgent}
                    disabled={isProcessing}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-400 hover:bg-green-500/10 rounded transition-colors disabled:opacity-50"
                  >
                    <UserPlus className="w-3 h-3" />
                    Assign
                  </button>
                </div>

                {projectAgents?.length > 0 ? (
                  <div className="space-y-2">
                    {projectAgents.map((agent) => (
                      <div
                        key={agent.id}
                        className="flex items-center justify-between px-3 py-2 bg-gray-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Bot className={`w-4 h-4 ${agent.isWorkingOnProject ? 'text-green-400' : 'text-gray-400'}`} />
                          <div>
                            <span className="text-sm text-gray-200">{agent.displayName || agent.name}</span>
                            {agent.isWorkingOnProject && (
                              <span className="ml-2 text-xs text-green-400">Working</span>
                            )}
                            <div className="text-xs text-gray-500">
                              {agent.roles?.map(r => r.displayName || r.name).join(', ')}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          {agent.isWorkingOnProject ? (
                            <button
                              type="button"
                              onClick={() => onDeactivateAgent(agent.id)}
                              disabled={isProcessing}
                              className="p-1.5 text-amber-400 hover:bg-amber-500/10 rounded transition-colors disabled:opacity-50"
                              title="Stop working"
                            >
                              <Pause className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => onActivateAgent(agent.id)}
                              disabled={isProcessing}
                              className="p-1.5 text-green-400 hover:bg-green-500/10 rounded transition-colors disabled:opacity-50"
                              title="Start working"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => onRemoveAgent(agent.id)}
                            disabled={isProcessing}
                            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                            title="Remove from project"
                          >
                            <UserMinus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-2">No agents assigned</p>
                )}
              </div>

              {/* Channels */}
              {projectChannels?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">
                    Channels ({projectChannels.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {projectChannels.map((channel) => (
                      <span
                        key={channel.id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-800 rounded text-sm text-gray-300"
                      >
                        <Hash className="w-3 h-3 text-gray-500" />
                        {channel.displayName || channel.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
});

// ============================================
// Main Component
// ============================================

export default function ProjectManager({ className = '' }) {
  // State
  const [projects, setProjects] = useState([]);
  const [expandedProjectId, setExpandedProjectId] = useState(null);
  const [projectDetails, setProjectDetails] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const [allAgents, setAllAgents] = useState([]);
  const [allRoles, setAllRoles] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [statusMessage, setStatusMessage] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, project: null });
  const [assignDialog, setAssignDialog] = useState({ isOpen: false, projectId: null, projectName: '' });

  // Effects
  useEffect(() => {
    loadProjects();
    loadAgentsAndRoles();
  }, [showArchived]);

  useEffect(() => {
    if (expandedProjectId) {
      loadProjectDetails(expandedProjectId);
    }
  }, [expandedProjectId]);

  // API Handlers
  const loadProjects = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchProjects(showArchived);
      setProjects(result.data || []);
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsLoading(false);
    }
  }, [showArchived]);

  const loadAgentsAndRoles = useCallback(async () => {
    try {
      const [agentsRes, rolesRes] = await Promise.all([
        fetchAgents(),
        fetchRoles()
      ]);
      setAllAgents(agentsRes.data || []);
      setAllRoles(rolesRes.data || []);
    } catch (err) {
      console.error('Failed to load agents/roles:', err);
    }
  }, []);

  const loadProjectDetails = useCallback(async (projectId) => {
    setIsLoadingDetails(true);
    try {
      const [agentsRes, channelsRes] = await Promise.all([
        fetchProjectAgents(projectId),
        fetchProjectChannels(projectId)
      ]);
      setProjectDetails((prev) => ({
        ...prev,
        [projectId]: {
          agents: agentsRes.data || [],
          channels: channelsRes.data || []
        }
      }));
    } catch (err) {
      console.error('Failed to load project details:', err);
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  const handleCreateProject = useCallback(async (data) => {
    setIsProcessing(true);
    try {
      await createProject(data);
      setStatusMessage({ type: 'success', message: `Project '${data.name}' created` });
      setShowCreateForm(false);
      await loadProjects();
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [loadProjects]);

  const handleUpdateProject = useCallback(async (data) => {
    if (!editingProject) return;
    setIsProcessing(true);
    try {
      await updateProject(editingProject.id, data);
      setStatusMessage({ type: 'success', message: `Project '${editingProject.name}' updated` });
      setEditingProject(null);
      await loadProjects();
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [editingProject, loadProjects]);

  const handleArchiveProject = useCallback(async (project) => {
    setIsProcessing(true);
    try {
      await updateProject(project.id, { isArchived: !project.isArchived });
      setStatusMessage({
        type: 'success',
        message: `Project '${project.name}' ${project.isArchived ? 'unarchived' : 'archived'}`
      });
      await loadProjects();
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [loadProjects]);

  const handleDeleteProject = useCallback(async () => {
    if (!deleteDialog.project) return;
    setIsProcessing(true);
    try {
      await deleteProject(deleteDialog.project.id);
      setStatusMessage({ type: 'success', message: `Project '${deleteDialog.project.name}' deleted` });
      setDeleteDialog({ isOpen: false, project: null });
      await loadProjects();
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [deleteDialog.project, loadProjects]);

  const handleAssignAgent = useCallback(async (agentId, roleId) => {
    if (!assignDialog.projectId) return;
    setIsProcessing(true);
    try {
      await assignAgentToProject(assignDialog.projectId, agentId, roleId);
      setStatusMessage({ type: 'success', message: 'Agent assigned to project' });
      setAssignDialog({ isOpen: false, projectId: null, projectName: '' });
      await loadProjectDetails(assignDialog.projectId);
      await loadProjects();
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [assignDialog.projectId, loadProjectDetails, loadProjects]);

  const handleRemoveAgent = useCallback(async (projectId, agentId) => {
    setIsProcessing(true);
    try {
      await removeAgentFromProject(projectId, agentId);
      setStatusMessage({ type: 'success', message: 'Agent removed from project' });
      await loadProjectDetails(projectId);
      await loadProjects();
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [loadProjectDetails, loadProjects]);

  const handleActivateAgent = useCallback(async (projectId, agentId) => {
    setIsProcessing(true);
    try {
      await activateAgentOnProject(projectId, agentId);
      setStatusMessage({ type: 'success', message: 'Agent activated on project' });
      await loadProjectDetails(projectId);
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [loadProjectDetails]);

  const handleDeactivateAgent = useCallback(async (projectId, agentId) => {
    setIsProcessing(true);
    try {
      await deactivateAgentFromProject(projectId, agentId);
      setStatusMessage({ type: 'success', message: 'Agent deactivated from project' });
      await loadProjectDetails(projectId);
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [loadProjectDetails]);

  // Computed Values
  const filteredProjects = useMemo(() => {
    if (!searchQuery) return projects;
    const query = searchQuery.toLowerCase();
    return projects.filter(p =>
      p.name.toLowerCase().includes(query) ||
      p.displayName?.toLowerCase().includes(query) ||
      p.description?.toLowerCase().includes(query)
    );
  }, [projects, searchQuery]);

  const assignedAgentIds = useMemo(() => {
    if (!assignDialog.projectId) return new Set();
    const details = projectDetails[assignDialog.projectId];
    return new Set(details?.agents?.map(a => a.id) || []);
  }, [assignDialog.projectId, projectDetails]);

  // Render
  return (
    <div className={`flex flex-col h-full bg-gray-900 ${className}`}>
      {/* Header */}
      <header className="flex-shrink-0 border-b border-gray-800">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <FolderKanban className="w-6 h-6 text-indigo-400" />
            <h1 className="text-xl font-semibold text-white">Project Manager</h1>
          </div>

          <button
            type="button"
            onClick={() => {
              setShowCreateForm(true);
              setEditingProject(null);
            }}
            disabled={isProcessing}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg disabled:opacity-50 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Project
          </button>
        </div>
      </header>

      {/* Status Messages */}
      {statusMessage && (
        <div className="px-6 pt-4">
          <StatusMessage
            type={statusMessage.type}
            message={statusMessage.message}
            onDismiss={() => setStatusMessage(null)}
          />
        </div>
      )}

      {/* Create/Edit Form */}
      {(showCreateForm || editingProject) && (
        <div className="px-6 pt-4">
          <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-300 mb-4">
              {editingProject ? 'Edit Project' : 'Create Project'}
            </h3>
            <ProjectForm
              project={editingProject}
              onSubmit={editingProject ? handleUpdateProject : handleCreateProject}
              onCancel={() => {
                setShowCreateForm(false);
                setEditingProject(null);
              }}
              isProcessing={isProcessing}
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Search and Filters */}
        <div className="flex items-center gap-4 px-6 py-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search projects..."
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-indigo-600 focus:ring-indigo-500"
            />
            Show Archived
          </label>
        </div>

        {/* Projects List */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FolderKanban className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No projects found</p>
              {!showCreateForm && (
                <button
                  type="button"
                  onClick={() => setShowCreateForm(true)}
                  className="mt-4 text-indigo-400 hover:text-indigo-300"
                >
                  Create your first project
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredProjects.map((project) => (
                <ProjectItem
                  key={project.id}
                  project={project}
                  isExpanded={expandedProjectId === project.id}
                  onToggle={() => setExpandedProjectId(
                    expandedProjectId === project.id ? null : project.id
                  )}
                  onEdit={(p) => {
                    setEditingProject(p);
                    setShowCreateForm(false);
                  }}
                  onArchive={handleArchiveProject}
                  onDelete={(p) => setDeleteDialog({ isOpen: true, project: p })}
                  projectAgents={projectDetails[project.id]?.agents}
                  projectChannels={projectDetails[project.id]?.channels}
                  onAssignAgent={() => setAssignDialog({
                    isOpen: true,
                    projectId: project.id,
                    projectName: project.displayName || project.name
                  })}
                  onRemoveAgent={(agentId) => handleRemoveAgent(project.id, agentId)}
                  onActivateAgent={(agentId) => handleActivateAgent(project.id, agentId)}
                  onDeactivateAgent={(agentId) => handleDeactivateAgent(project.id, agentId)}
                  isLoadingDetails={isLoadingDetails && expandedProjectId === project.id}
                  isProcessing={isProcessing}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <DeleteConfirmModal
        isOpen={deleteDialog.isOpen}
        projectName={deleteDialog.project?.displayName || deleteDialog.project?.name}
        onConfirm={handleDeleteProject}
        onCancel={() => setDeleteDialog({ isOpen: false, project: null })}
        isProcessing={isProcessing}
      />

      <AssignAgentModal
        isOpen={assignDialog.isOpen}
        projectName={assignDialog.projectName}
        agents={allAgents}
        roles={allRoles}
        assignedAgentIds={assignedAgentIds}
        onAssign={handleAssignAgent}
        onCancel={() => setAssignDialog({ isOpen: false, projectId: null, projectName: '' })}
        isProcessing={isProcessing}
      />
    </div>
  );
}

// Named Exports
export {
  ProjectForm,
  ProjectItem,
  AssignAgentModal,
  DeleteConfirmModal,
  StatusMessage,
  PROJECT_STATUS
};
